import { useAssetStore } from '@/stores/api/asset';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { drawCrosshatch } from '@/threejs/canvas-patterns/test-projections/crosshatch';
import { drawDisguiseLogo } from '@/threejs/canvas-patterns/utils/draw-disguise-logo';
import { applyOverlaysToObject, render } from '@/threejs/core';
import { edgesManager } from '@/threejs/managers/edgesManager';
import { getObjectByCode } from '@/threejs/objectManager';
import { textureManager } from '@/threejs/textureManager';
import { getAssetTypeFromFile } from '@/utils/getAssetTypeFromFile';
import gsap from 'gsap';
import * as THREE from 'three';

let _cachedDefaultMapTexture = null;

function createDefaultMapTexture() {
  if (!_cachedDefaultMapTexture) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, 512, 512);
    drawCrosshatch(ctx, 512, 512, 0, 0);
    drawDisguiseLogo(ctx, 512, 512, 0, 0);
    _cachedDefaultMapTexture = new THREE.CanvasTexture(canvas);
  }
  return _cachedDefaultMapTexture;
}
export class BaseObject extends THREE.Mesh {
  constructor(geometry, material) {
    super(geometry, material);
  }

  setColor(color) {
    if (this.material) {
      this.material.color.set(new THREE.Color(color));
    }
  }

  getColor() {
    return this.material ? this.material.color.getHex() : null;
  }

  setSelected(selected) {
    this.userData.selected = selected;
  }

  isSelected() {
    return this.userData.selected;
  }

  select() {
    this.setSelected(true);
  }

  clearSelection() {
    this.setSelected(false);
  }
  removeOutline() {
    this.userData.edgeOutline = false;
    const outlinesToRemove = this.children.filter((child) => child.constructor.name === 'FatLinesBatch');

    outlinesToRemove.forEach((outline) => {
      this.remove(outline);
      // Dispose of outline resources if needed
      if (outline.dispose) {
        outline.dispose();
      }
    });
  }
  getOutline() {
    this.removeOutline();

    this.userData.edgeOutline = true;
    this.traverse((child) => {
      child.userData.edgeOutline = true;
    });
    edgesManager.refreshFlb();
  }

  applyProperties(properties) {
    // To be overridden by subclasses
    applyProperties(this, properties, false);
  }
}

// Common default properties for all objects
export const DEFAULT_PROPS = {
  name: 'New Object',
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 1, y: 1, z: 1 },
};

/**
 * Applies a numeric geometry property from `properties` onto `obj`.
 * Returns `true` if the property was present and changed (i.e. geometry needs rebuilding).
 */
export const applyGeometryProperty = (obj, value, key, min) => {
  if (typeof value !== 'number' || typeof min !== 'number') return false;
  if (!isFinite(value)) return false;
  if (value < min) return false;
  if (value === obj[key]) return false;

  obj.userData.data[key] = value;
  obj[key] = value;
  return true;
};

// Function to apply properties to an object
export const applyProperties = (object, properties, animate = true) => {
  // Find the mesh inside the group if this is a group

  if (!object) return;
  if (!object.userData) object.userData = {};
  if (!object.userData.data) object.userData.data = {};

  const mesh = object.children?.find((child) => child instanceof THREE.Mesh) || object;

  if (
    'position.x' in properties ||
    'position.y' in properties ||
    'position.z' in properties ||
    'position[x]' in properties ||
    'position[y]' in properties ||
    'position[z]' in properties
  ) {
    // Initialize position if it doesn't exist
    if (!object.userData.data.position) {
      object.userData.data.position = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties['position.x'] !== object.userData.data.position.x ||
      properties['position.y'] !== object.userData.data.position.y ||
      properties['position.z'] !== object.userData.data.position.z ||
      properties['position[x]'] !== object.userData.data.position.x ||
      properties['position[y]'] !== object.userData.data.position.y ||
      properties['position[z]'] !== object.userData.data.position.z;

    if (needsUpdate) {
      object.userData.data.position.x =
        Number(properties['position.x'] ?? properties['position[x]']) ?? object.userData.data.position.x ?? 0;
      object.userData.data.position.y =
        Number(properties['position.y'] ?? properties['position[y]']) ?? object.userData.data.position.y ?? 0;
      object.userData.data.position.z =
        Number(properties['position.z'] ?? properties['position[z]']) ?? object.userData.data.position.z ?? 0;

      animateObject(object.userData.code, { position: object.userData.data.position }, animate ? 0.1 : 0);
    }
  }

  if ('position' in properties) {
    // Initialize position if it doesn't exist
    if (!object.userData.data.position) {
      object.userData.data.position = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties.position.x !== object.userData.data.position.x ||
      properties.position.y !== object.userData.data.position.y ||
      properties.position.z !== object.userData.data.position.z;

    if (needsUpdate) {
      object.userData.data.position.x = Number(properties.position?.x ?? object.userData.data.position.x ?? 0);
      object.userData.data.position.y = Number(properties.position?.y ?? object.userData.data.position.y ?? 0);
      object.userData.data.position.z = Number(properties.position?.z ?? object.userData.data.position.z ?? 0);

      animateObject(object.userData.code, { position: object.userData.data.position }, animate ? 0.1 : 0);
    }
  }

  if (
    'rotation.x' in properties ||
    'rotation.y' in properties ||
    'rotation.z' in properties ||
    'rotation[x]' in properties ||
    'rotation[y]' in properties ||
    'rotation[z]' in properties
  ) {
    // Initialize rotation if it doesn't exist
    if (!object.userData.data.rotation) {
      object.userData.data.rotation = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties['rotation.x'] !== object.userData.data.rotation.x ||
      properties['rotation.y'] !== object.userData.data.rotation.y ||
      properties['rotation.z'] !== object.userData.data.rotation.z ||
      properties['rotation[x]'] !== object.userData.data.rotation.x ||
      properties['rotation[y]'] !== object.userData.data.rotation.y ||
      properties['rotation[z]'] !== object.userData.data.rotation.z;
    if (needsUpdate) {
      object.userData.data.rotation.x =
        Number(properties['rotation.x'] ?? properties['rotation[x]']) ?? object.userData.data.rotation.x ?? 0;
      object.userData.data.rotation.y =
        Number(properties['rotation.y'] ?? properties['rotation[y]']) ?? object.userData.data.rotation.y ?? 0;
      object.userData.data.rotation.z =
        Number(properties['rotation.z'] ?? properties['rotation[z]']) ?? object.userData.data.rotation.z ?? 0;
      animateObject(object.userData.code, { rotation: object.userData.data.rotation }, animate ? 0.1 : 0);
    }
  }

  if ('rotation' in properties) {
    // Initialize rotation if it doesn't exist
    if (!object.userData.data.rotation) {
      object.userData.data.rotation = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties.rotation.x !== object.userData.data.rotation.x ||
      properties.rotation.y !== object.userData.data.rotation.y ||
      properties.rotation.z !== object.userData.data.rotation.z;
    if (needsUpdate) {
      object.userData.data.rotation.x = Number(properties.rotation?.x ?? object.userData.data.rotation.x ?? 0);
      object.userData.data.rotation.y = Number(properties.rotation?.y ?? object.userData.data.rotation.y ?? 0);
      object.userData.data.rotation.z = Number(properties.rotation?.z ?? object.userData.data.rotation.z ?? 0);
      animateObject(object.userData.code, { rotation: object.userData.data.rotation }, animate ? 0.1 : 0);
    }
  }

  if ('scale' in properties) {
    // Initialize scale if it doesn't exist
    if (!object.userData.data.scale) {
      object.userData.data.scale = { x: 1, y: 1, z: 1 };
    }

    // Handle both numeric and vector3 scale values
    const scaleValue =
      typeof properties.scale === 'number'
        ? { x: properties.scale, y: properties.scale, z: properties.scale }
        : properties.scale;

    const needsUpdate =
      (scaleValue?.x ?? object.userData.data.scale.x) !== object.userData.data.scale.x ||
      (scaleValue?.y ?? object.userData.data.scale.y) !== object.userData.data.scale.y ||
      (scaleValue?.z ?? object.userData.data.scale.z) !== object.userData.data.scale.z;

    if (needsUpdate) {
      object.userData.data.scale.x = Number(scaleValue?.x ?? object.userData.data.scale.x ?? 1);
      object.userData.data.scale.y = Number(scaleValue?.y ?? object.userData.data.scale.y ?? 1);
      object.userData.data.scale.z = Number(scaleValue?.z ?? object.userData.data.scale.z ?? 1);

      animateObject(object.userData.code, { scale: object.userData.data.scale }, animate ? 0.1 : 0);
    }
  }
  if ('name' in properties && object.name !== properties.name) {
    object.name = properties.name;
  }

  if ('color' in properties) {
    if (mesh.material && properties.color) {
      const color = properties.color.startsWith('#') ? properties.color : `#${properties.color}`;

      const newColor = new THREE.Color(color);
      if (!mesh.material.color.equals(newColor)) {
        mesh.material.color.set(newColor);

        // Update emissive color if luminance is active
        const luminance = properties.luminance || mesh.userData.luminance || 0;
        if (luminance > 0 && properties.material !== 'map') {
          mesh.material.emissive = mesh.material.color.clone();
          mesh.material.emissiveIntensity = luminance / 100;
          mesh.material.needsUpdate = true;
        }
      }
    }
  }

  if ('luminance' in properties) {
    if (mesh.material) {
      const luminance = properties.luminance || 0;

      // Only update if luminance has changed
      if (mesh.userData.luminance !== luminance) {
        // Store luminance in userData for persistence
        mesh.userData.luminance = luminance;

        // Set emissive color to match the material color (or white if using a map)
        if (luminance > 0) {
          if (properties.material === 'map' && mesh.material.map) {
            // For textured materials, use white emissive
            mesh.material.emissive = new THREE.Color(0xffffff);
          } else {
            // For colored materials, use the same color for emissive
            mesh.material.emissive = mesh.material.color.clone();
          }
          // Convert luminance (cd/m2) to emissive intensity
          // Scale factor to make the values work well visually
          mesh.material.emissiveIntensity = luminance / 100;
        } else {
          // Turn off emissive when luminance is 0
          mesh.material.emissive = new THREE.Color(0x000000);
          mesh.material.emissiveIntensity = 0;
        }
        mesh.material.needsUpdate = true;
      }
    }
  }

  if ('visible' in properties && object.visible !== properties.visible) {
    object.visible = properties.visible;
  }

  if ('lock' in properties && object.userData.lock !== properties.lock) {
    object.userData.lock = properties.lock;
  }

  if ('side' in properties) {
    if (mesh.material && mesh.material.side !== properties.side) {
      mesh.material.side = properties.side;
    }
  }

  if ('material' in properties) {
    if (mesh.material) {
      if (properties.material === 'map') {
        // Skip edge, overlap, and pixel-size overlays on map-material meshes to stay
        // within the 16 texture-unit GPU limit (the gradient overlay alone needs ~9 slots).
        mesh.userData.projectorEdgeIgnore = true;
        mesh.userData.projectorOverlapIgnore = true;
        mesh.userData.projectorPixelSizeIgnore = true;

        // Replace material with a clean MeshStandardMaterial for the map texture
        const oldMaterial = mesh.material;
        mesh.material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          side: oldMaterial.side,
          wireframe: oldMaterial.wireframe,
          transparent: oldMaterial.transparent,
          opacity: oldMaterial.opacity,
        });
        oldMaterial.dispose();

        // Re-inject projector overlay + gradient shaders so projections still appear
        applyOverlaysToObject(mesh);

        // Show default pattern while no asset is selected or while loading
        const defaultTexture = createDefaultMapTexture();
        mesh.material.map = defaultTexture;
        mesh.material.needsUpdate = true;
        render();

        const applyEmissive = (texture) => {
          const luminance = properties.luminance || mesh.userData.luminance || 0;
          if (luminance > 0) {
            mesh.material.emissive = new THREE.Color(0xffffff);
            mesh.material.emissiveMap = texture;
            mesh.material.emissiveIntensity = luminance / 100;
          }
        };

        const applyTextureFromUrl = (url) => {
          const fileName = url.split('?')[0].split('/').pop();
          if (getAssetTypeFromFile({ name: fileName }) === 'video') {
            const video = textureManager.getVideoElement(url);
            if (video) {
              const texture = textureManager.getVideoTexture(url);
              mesh.material.map = texture;
              mesh.material.needsUpdate = true;
              applyEmissive(texture);

              // Ensure video starts paused — only the play button should start it.
              video.autoplay = false;
              video.loop = false;
              video.pause();

              const timelineStore = useTimelinePlayerStore();

              // Continuous render loop that syncs with timeline state (matches LED approach).
              const startVideoRenderLoop = () => {
                const loop = () => {
                  if (!mesh.material) {
                    mesh.userData._mapVideoRAF = null;
                    return;
                  }

                  // Sync play/pause with timeline state
                  if (timelineStore.isPlaying) {
                    if (video.paused) {
                      video.play().catch(() => {});
                    }
                    // Manual loop: restart when reaching end (same threshold as LED)
                    if (video.duration && video.currentTime >= video.duration - 0.1) {
                      video.currentTime = 0;
                    }
                  } else {
                    if (!video.paused) {
                      video.pause();
                    }
                  }

                  if (video.readyState >= video.HAVE_CURRENT_DATA) {
                    mesh.material.needsUpdate = true;
                    render();
                  }

                  mesh.userData._mapVideoRAF = requestAnimationFrame(loop);
                };
                if (!mesh.userData._mapVideoRAF) {
                  mesh.userData._mapVideoRAF = requestAnimationFrame(loop);
                }
              };

              const stopVideoRenderLoop = () => {
                if (mesh.userData._mapVideoRAF) {
                  cancelAnimationFrame(mesh.userData._mapVideoRAF);
                  mesh.userData._mapVideoRAF = null;
                }
              };

              // Store cleanup so it can be called when material switches away from map
              mesh.userData._mapVideoCleanup = () => {
                stopVideoRenderLoop();
              };

              // Start the render loop once the video has data
              if (video.readyState >= video.HAVE_CURRENT_DATA) {
                startVideoRenderLoop();
              } else {
                video.addEventListener('loadeddata', () => startVideoRenderLoop(), { once: true });
              }
            }
          } else {
            textureManager
              .getImageTexture(url)
              .then((texture) => {
                if (!mesh.material) return;
                mesh.material.map = texture;
                mesh.material.needsUpdate = true;
                applyEmissive(texture);

                render();
              })
              .catch(() => {});
          }
        };

        if (properties.url) {
          applyTextureFromUrl(properties.url);
        } else if (properties.resource_id) {
          const assetStore = useAssetStore();
          assetStore.fetch(properties.resource_id).then((asset) => {
            if (asset?.latest_version?.file && mesh.material) {
              applyTextureFromUrl(asset.latest_version.file);
            }
          });
        }
      } else if (properties.material === 'color') {
        // Clean up any running map-video render loop
        mesh.userData._mapVideoCleanup?.();

        // Clear all per-overlay ignore flags so every overlay re-applies
        mesh.userData.projectorOverlayIgnore = false;
        mesh.userData.projectorEdgeIgnore = false;
        mesh.userData.projectorOverlapIgnore = false;
        mesh.userData.projectorPixelSizeIgnore = false;
        const oldMaterial = mesh.material;
        // Dispose textures before disposing material (Three.js doesn't cascade)
        if (oldMaterial.map) oldMaterial.map.dispose();
        if (oldMaterial.emissiveMap) oldMaterial.emissiveMap.dispose();
        const colorValue = properties.color
          ? properties.color.startsWith('#')
            ? properties.color
            : `#${properties.color}`
          : '#ffffff';
        mesh.material = new THREE.MeshStandardMaterial({
          color: colorValue,
          side: oldMaterial.side,
          wireframe: oldMaterial.wireframe,
          transparent: oldMaterial.transparent,
          opacity: oldMaterial.opacity,
        });
        oldMaterial.dispose();
        mesh.material.needsUpdate = true;

        // Re-inject projector overlay shaders onto the fresh material
        applyOverlaysToObject(mesh);
      }

      // Update emissive based on current luminance when material changes (skip for map mode — handled after texture loads)
      if (properties.material !== 'map') {
        if ('luminance' in properties || mesh.userData.luminance) {
          const luminance = properties.luminance || mesh.userData.luminance || 0;
          if (luminance > 0) {
            mesh.material.emissive = mesh.material.color.clone();
            mesh.material.emissiveIntensity = luminance / 100;
          }
        }
      }
    }
  }

  if ('wireframe' in properties) {
    if (mesh.material) {
      mesh.material.wireframe = properties.wireframe;
      mesh.material.needsUpdate = true;
    }
  }

  if ('repeatX' in properties || 'repeatY' in properties) {
    if (mesh.material && mesh.material.map) {
      const newRepeatX = properties.repeatX || 1;
      const newRepeatY = properties.repeatY || 1;

      if (mesh.material.map.repeat.x !== newRepeatX || mesh.material.map.repeat.y !== newRepeatY) {
        mesh.material.map.repeat.set(newRepeatX, newRepeatY);
        mesh.material.map.needsUpdate = true;
      }
    }
  }

  if ('wrappingX' in properties || 'wrappingY' in properties) {
    if (mesh.material && mesh.material.map) {
      const getWrappingMode = (value) => {
        switch (value) {
          case 'repeat':
            return THREE.RepeatWrapping;
          case 'mirror':
            return THREE.MirroredRepeatWrapping;
          case 'normal':
          default:
            return THREE.ClampToEdgeWrapping;
        }
      };

      const newWrapS = getWrappingMode(properties.wrappingX);
      const newWrapT = getWrappingMode(properties.wrappingY);

      if (mesh.material.map.wrapS !== newWrapS || mesh.material.map.wrapT !== newWrapT) {
        mesh.material.map.wrapS = newWrapS;
        mesh.material.map.wrapT = newWrapT;
        mesh.material.map.needsUpdate = true;
      }
    }
  }

  if ('offsetX' in properties || 'offsetY' in properties) {
    if (mesh.material && mesh.material.map) {
      const newOffsetX = properties.offsetX || 0;
      const newOffsetY = properties.offsetY || 0;

      if (mesh.material.map.offset.x !== newOffsetX || mesh.material.map.offset.y !== newOffsetY) {
        mesh.material.map.offset.set(newOffsetX, newOffsetY);
        mesh.material.map.needsUpdate = true;
      }
    }
  }

  if ('playSource' in properties) {
    // Handle video playback control
    if (properties.playSource === 'stop') {
      object.stop();
    } else {
      object.play();
    }
  }

  if ('applyProperties' in object) {
    //console.warn('Calling super applyProperties on object', object)
    //object.applyProperties(properties)
  }

  render();
};

// Function to animate object properties
export const animateObject = (id, properties, duration = 0.1, ease = 'power2.inOut') => {
  const object = getObjectByCode(id);
  if (!object) return;

  // Create a GSAP timeline for the animation
  const timeline = gsap.timeline();

  // Animate position if provided
  if (properties.position) {
    if (duration === 0) {
      // Set position synchronously for duration 0 (avoids gsap async timing issues)
      object.position.set(
        properties.position.x ?? object.position.x ?? 0,
        properties.position.y ?? object.position.y ?? 0,
        properties.position.z ?? object.position.z ?? 0
      );
      object.updateMatrixWorld(true);
      render();
      edgesManager.refreshFlb();
    } else {
      timeline.to(object.position, {
        x: properties.position.x ?? object.position.x ?? 0,
        y: properties.position.y ?? object.position.y ?? 0,
        z: properties.position.z ?? object.position.z ?? 0,
        duration,
        ease,
        onUpdate: () => {
          render();
          edgesManager.refreshFlb();
        },
      });
    }
  }

  // Animate rotation if provided
  if (properties.rotation) {
    // Convert rotation values from degrees to radians
    const rotationInRadians = {
      x: THREE.MathUtils.degToRad(properties.rotation.x || object.rotation.x || 0),
      y: THREE.MathUtils.degToRad(properties.rotation.y || object.rotation.y || 0),
      z: THREE.MathUtils.degToRad(properties.rotation.z || object.rotation.z || 0),
    };

    if (duration === 0) {
      object.rotation.set(rotationInRadians.x, rotationInRadians.y, rotationInRadians.z);
      object.updateMatrixWorld(true);
      render();
    } else {
      timeline.to(
        object.rotation,
        {
          x: rotationInRadians.x,
          y: rotationInRadians.y,
          z: rotationInRadians.z,
          duration,
          ease,
          onUpdate: () => render(),
        },
        '<'
      );
    }
  }

  // Animate scale if provided
  if (properties.scale) {
    if (duration === 0) {
      object.scale.set(
        properties.scale.x || object.scale.x || 1,
        properties.scale.y || object.scale.y || 1,
        properties.scale.z || object.scale.z || 1
      );
      object.updateMatrixWorld(true);
      render();
    } else {
      timeline.to(
        object.scale,
        {
          x: properties.scale.x || object.scale.x || 1,
          y: properties.scale.y || object.scale.y || 1,
          z: properties.scale.z || object.scale.z || 1,
          duration,
          ease,
          onUpdate: () => render(),
        },
        '<'
      );
    }
  }

  return timeline;
};

// Function to stop all animations on an object
export const stopObjectAnimations = (id) => {
  const object = getObjectByCode(id);
  if (!object) return;

  gsap.killTweensOf(object.position);
  gsap.killTweensOf(object.rotation);
  gsap.killTweensOf(object.scale);
};
